import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Kodefelt, Oppsettshelt, useAppoppsett, Visningsknapp } from '../auth/Appoppsett.tsx';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useEnhet } from '../auth/enhet.ts';
import { AuthFeil, type Faktorer, type Faktoroppforing } from '../auth/typer.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Seksjon } from '../innstillinger/Rad.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useSidetittel } from '../skall/tittel.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Radmeny } from './Radmeny.tsx';

/* ------------------------------------------------------------------------------
   INNSPEL — «MIN INNLOGGING». Kontoen din, og hvordan du bekrefter at det er deg.

   HVORFOR SIDEN FINNES: enheten som andre faktor ble bygget, men oppsettskjermen foran
   låsen vises bare for en konto UTEN en verifisert metode. Den som har autentiseringsappen
   fra før går rett til utfordringen og ser aldri oppsettet — så uten et sted å legge til en
   metode MENS man er innlogget, var enheten utilgjengelig for nettopp dem som har hatt
   panelet lengst. Alternativet — å fjerne metoden i leverandørens konsoll og la dem møte
   oppsettet på nytt — ble forkastet av produkteier.

   HVOR DEN HØRER: ingen av de to innstillingssidene. Innstillinger er ROMMETS og
   Organisasjon er EIERENS; dette er BRUKERENS egen konto, og en «Endre»-rad som bare gjelder
   den som ser den hører ikke sammen med noe en leietaker konfigurerer. Den nås fra «⋯» i
   skallets fot — der rollen, adressen, paletten og «Logg ut» alt bor (DD-48) — og er derfor
   ikke en av de fem sidene i rommet (DD-01).

   FORMEN ER INNSTILLINGENES RAD (R5.3): navn, verdi i én lesbar linje, og handlingen til
   høyre. Radkomponenten selv er ikke gjenbrukt — `innstillinger/Rad.tsx` har «Endre»/«Vis»
   og er bundet til rollen (eier) og til sidens utkast, og ingen av de tre gjelder her: det
   finnes ikke noe utkast å lagre, hver handling skjer med én gang hos leverandøren, og alle
   roller styrer sin egen innlogging. `Seksjon` og radens CSS-klasser er delt.

   OPPSETTET AV APPEN ER DEN SAMME KOMPONENTEN som foran låsen — `auth/Appoppsett.tsx`, delt
   med `sider/Tofaktor.tsx`. QR-en, nøkkelvisningen, `otpauth://`-lenken på en berøringsenhet
   og feltet finnes ÉTT sted, så ordene endres ett sted.

   ORDENE (DD-60): «Denne enheten» og «Koden fra appen». «WebAuthn», «passkey» og «faktor»
   er utviklerens ord og står ikke på skjermen. «Lukk» lukker oppsettet uten å lagre;
   «Avbryt» er bekreftelsens nei-knapp.

   DE TO HARDE REGLENE FOR FJERNING:
     1. Den SISTE metoden kan aldri fjernes. Knappen FINNES IKKE — den er ikke grå, den er
        borte — og raden sier hvorfor i én setning. En grå knapp ville invitert til et trykk
        som aldri kan lykkes; leverandøren ville dessuten latt henne fjerne den og sendt
        henne rett i oppsettet neste gang, uten at hun forsto hvorfor.
     2. Å fjerne én av to krever en bekreftelse som sier HVA SOM SKJER — hvilken metode hun
        bekrefter med etterpå, og at den fjernede må legges til på nytt. Handlingen kan ikke
        angres, så den får en dialog og ikke en angre-toast.

   SESJONEN ER ALLEREDE `aal2` HER: siden ligger bak låsen, og den som ser den har nettopp
   bekreftet. Derfor ingen ny utfordring før en endring — verken før «Legg til» eller før
   «Fjern». Sier vi det på skjermen, forklarer vi en lås brukeren allerede har åpnet.

   VI SKRIVER INGEN EGEN HENDELSE: dette er leverandørens side av innloggingen, og deres
   egen auth-logg fanger registrering og avregistrering. Plattformens append-only logg eier
   det som skjer med SAKENE.

   ENHETEN LIGGER BAK ET KONFIGFLAGG, OG DET ER AV (`src/auth/enhet.ts`,
   `VITE_INNSPEL_DEVICE_FACTOR`). Supabase støtter ikke faktoren ennå — Management API-et
   svarer «Enabling of MFA with WebAuthn not currently supported» (2026-09-21,
   docs/drift/supabase-enhetsfaktor.md). Med flagget av består siden derfor av ÉN rad,
   «Koden fra appen», og enheten nevnes ikke: en rad som bare kunne tilbudt noe serveren
   avviser er ikke en opplysning om kontoen, men om vår utrulling. En enhet som ALLEREDE
   er registrert vises likevel — siden viser hva brukeren har, ikke hva vi tilbyr.

   DD-08: hver tilstand har ikon OG tekst — «Lagt til …» med hake, «Ikke lagt til» med en
   tom ring. Fargen bærer aldri meningen alene.
   DD-58: ett primærvalg. «Legg til» er en tekstknapp i radens handlingskolonne, som «Endre»
   er på Innstillinger; den ene fylte knappen på siden er «Fullfør oppsettet» inne i den
   åpnede raden.
   DD-53: «Fjern» er rød — den fjerner noe HOS LEVERANDØREN og kan ikke angres, til forskjell
   fra innstillingssidenes «Fjern», som bare endrer et utkast. Men rødfargen står ikke i
   raden: den bor inne i radens «⋯» (`Radmeny.tsx`, `ux-flow-architect` «Meny fra en knapp»),
   fordi en rød tekstknapp per rad gjorde den mest gjentatte flaten til den farligste.
------------------------------------------------------------------------------ */

/** Setningen som står der «Fjern» ville stått på den siste metoden. */
const SISTE = 'Du må ha minst én måte å bekrefte på.';

/**
 * …og veien videre, som bare står når det FINNES en metode til å legge til. Med
 * enhetsfaktoren av er appen den eneste metoden panelet har, og «Legg til en til» ville
 * bedt om noe brukeren ikke kan gjøre — samme feil som en død knapp, i prosa.
 */
const SISTE_MED_VEI = `${SISTE} Legg til en til for å kunne fjerne denne.`;

/** Nettleseren kan ikke gjøre seremonien. Sies som en grunn, ikke som en død knapp. */
const UTEN_STOETTE = 'Denne nettleseren kan ikke bekrefte med enheten. Prøv en annen, eller bruk koden fra appen.';

/** «18. september 2026». Måneden med bokstaver: et rent tallformat kan leses i to rekkefølger. */
const dato = (iso: string) => new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });

/** Metodenes navn ett sted — radnavnet, handlingenes etiketter og bekreftelsen leser alle herfra. */
const NAVN: Record<'enhet' | 'app', string> = { enhet: 'denne enheten', app: 'koden fra appen' };

/** Radnavnet er det samme med stor forbokstav. */
const radnavn = (type: 'enhet' | 'app') => `${NAVN[type][0]!.toUpperCase()}${NAVN[type].slice(1)}`;

/**
 * Én metode som rad. Samme tre kolonner som innstillingsradene, og grunnen — når det finnes
 * en — på egen linje under verdien i `body` (DD-67: det brukeren avgjør på står aldri i
 * småtekst).
 */
function Metoderad({ type, lagtTil, grunn, handling, children }: { type: 'enhet' | 'app'; lagtTil: string | null; grunn: string | null; handling: ReactNode; children?: ReactNode }) {
  return (
    <div className="panel-rad-innst" data-testid={`rad-${type}`}>
      <span className="panel-rad-innst-navn">
        <span className="panel-rad-innst-navn-tekst">{radnavn(type)}</span>
      </span>
      <span className="panel-rad-innst-verdi" data-testid={`rad-verdi-${type}`}>
        {lagtTil ? (
          <span className="panel-innst-status panel-innst-ok">
            <Ikon navn="checkmark-circle-outline" /> Lagt til {dato(lagtTil)}
          </span>
        ) : (
          <span className="panel-innst-status panel-innst-av">
            <Ikon navn="ellipse-outline" /> Ikke lagt til
          </span>
        )}
        {grunn ? <div className="panel-grunn">{grunn}</div> : null}
      </span>
      <span className="panel-rad-innst-handling">{handling}</span>
      {children}
    </div>
  );
}

/** Det som står der «Fjern» ville stått på den siste metoden: hengelås OG ordet (DD-08). */
function KanIkkeFjernes() {
  return (
    <span className="panel-innst-laast">
      <Ikon navn="lock-closed-outline" /> Kan ikke fjernes
    </span>
  );
}

export function MinInnlogging() {
  const auth = useAuth();
  useSidetittel('Min innlogging');

  /**
   * De to ja-ene for enheten, hentet ÉN gang (`auth/enhet.ts`):
   *   `tilbys` — utrullingen er på. Er den av, NEVNES enheten ikke på siden i det hele
   *              tatt: Supabase støtter ikke faktoren ennå, og en rad med «Legg til» som
   *              alltid ender i en avvisning er verre enn ingen rad.
   *   `mulig`  — utrullingen er på OG nettleseren kan seremonien. Er bare nettleseren
   *              imot, står raden med grunnen i stedet for knappen.
   */
  const { tilbys: enhetTilbys, mulig: enhetMulig } = useEnhet();
  const [faktorer, setFaktorer] = useState<Faktorer | null>(null);
  const [lastefeil, setLastefeil] = useState<string | null>(null);
  /** Raden som står åpen med oppsettet. Bare appen har et oppsett å åpne. */
  const [aapen, setAapen] = useState(false);
  const [fjerner, setFjerner] = useState<Faktoroppforing | null>(null);
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [kode, setKode] = useState('');
  const [kodefeil, setKodefeil] = useState<string | null>(null);
  /** StrictMode kjører effekten to ganger; løftet i en ref gir ett oppslag, ikke to. */
  const løftet = useRef<Promise<Faktorer> | null>(null);

  const les = useCallback(
    (paaNytt: boolean) => {
      if (paaNytt) løftet.current = null;
      const løfte = (løftet.current ??= auth.hentFaktorer());
      let levende = true;
      løfte
        .then((f) => {
          if (!levende) return;
          setFaktorer(f);
          setLastefeil(null);
        })
        .catch((e: unknown) => {
          if (levende) setLastefeil(e instanceof AuthFeil ? e.message : 'Vi fikk ikke sjekket hvordan du bekrefter. Prøv igjen.');
        });
      return () => {
        levende = false;
      };
    },
    [auth],
  );

  useEffect(() => les(false), [les]);

  /** Etter en endring er svaret fra før foreldet: leverandøren spørres på nytt, aldri gjettes. */
  const oppfrisk = useCallback(async () => {
    løftet.current = null;
    setFaktorer(await auth.hentFaktorer());
  }, [auth]);

  const { oppsett, startfeil, visning, setVisning, paaNytt } = useAppoppsett(aapen);

  /** Grunnen på den siste metoden — med veien videre bare når en annen metode kan legges til. */
  const siste = enhetTilbys ? SISTE_MED_VEI : SISTE;

  const enhet = faktorer?.liste.find((f) => f.type === 'enhet') ?? null;
  const app = faktorer?.liste.find((f) => f.type === 'app') ?? null;
  const antall = faktorer?.liste.length ?? 0;

  /** Seremonien eies av porten; her er bare tilstanden rundt den. */
  const leggTilEnhet = () => {
    setFeil(null);
    setOpptatt(true);
    auth
      .settOppEnhet()
      .then(oppfrisk)
      // KUN `AuthFeil` — panelets egne setninger. En `DOMException` fra nettleseren
      // («The operation either timed out or was not allowed.») er maskinens ord.
      .catch((e: unknown) => setFeil(e instanceof AuthFeil ? e.message : 'Enheten bekreftet ikke. Prøv igjen.'))
      .finally(() => setOpptatt(false));
  };

  const fullfoerApp = (e: FormEvent) => {
    e.preventDefault();
    if (!oppsett) return;
    setKodefeil(null);
    setOpptatt(true);
    auth
      .bekreftOppsett(oppsett.faktorId, kode)
      .then(async () => {
        await oppfrisk();
        setAapen(false);
        setKode('');
      })
      .catch((err: unknown) => setKodefeil(err instanceof Error ? err.message : 'Koden ble ikke godtatt.'))
      .finally(() => setOpptatt(false));
  };

  const fjern = (f: Faktoroppforing) => {
    setFeil(null);
    setOpptatt(true);
    auth
      .fjernFaktor(f.id)
      .then(oppfrisk)
      .catch((e: unknown) => setFeil(e instanceof AuthFeil ? e.message : 'Vi fikk ikke fjernet den. Prøv igjen om litt.'))
      .finally(() => {
        setOpptatt(false);
        setFjerner(null);
      });
  };

  /**
   * «Fjern» bor i radens «⋯», ikke i raden (produkteier 2026-09-21). En rød tekstknapp per
   * rad gjorde den mest gjentatte flaten på siden til den farligste, og fargen sto i hvile
   * på en side der ingenting haster. Rødfargen ble med INN i menyen, der den bæres av ett
   * valg og ikke av flaten. Menyen finnes bare når noe annet blir igjen å bekrefte med —
   * en «⋯» uten et valg bak er et løfte som ikke holdes.
   */
  const radmeny = (f: Faktoroppforing) => <Radmeny etikett={NAVN[f.type]} testid={f.type} valg={[{ navn: `Fjern ${NAVN[f.type]}`, ikon: 'trash-outline', fare: true, onVelg: () => setFjerner(f), testid: `fjern-${f.type}` }]} />;

  if (lastefeil) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Min innlogging</h1>
        </header>
        <div role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> {lastefeil}
          <button type="button" className="panel-knapp-liten" onClick={() => les(true)}>
            Prøv igjen
          </button>
        </div>
      </>
    );
  }

  if (!faktorer) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Min innlogging</h1>
        </header>
        <div role="status" aria-busy="true" aria-label="Henter måtene du kan bekrefte på" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
        </div>
      </>
    );
  }

  return (
    <>
      <header className="panel-topp">
        <h1 className="panel-h1">Min innlogging</h1>
      </header>

      <div className="panel-innst">
        <div className="panel-innst-hode">
          <p className="panel-innst-lead">Kontoen din, og hvordan du bekrefter at det er deg. Gjelder bare deg — ingen andre ser eller endrer dette.</p>
        </div>

        {/* Feil fra en handling står over radene, ikke inne i en av dem: den kan komme fra
            begge, og en melding som flytter seg er vanskeligere å finne igjen. */}
        {feil ? (
          <p role="alert" className="panel-melding panel-melding-feil" data-testid="handlingsfeil">
            <Ikon navn="warning-outline" /> {feil}
          </p>
        ) : null}

        <Seksjon id="bekreft" tittel="Hvordan du bekrefter">
          {/* Raden finnes når brukeren HAR en enhet, eller når enheten kan legges til.
              Er utrullingen av og hun ikke har en, er raden borte — ikke tom, ikke grå.
              Siden viser hva hun faktisk har; en rad som bare kan si «ikke mulig ennå»
              ville vært en opplysning om vår utrulling, ikke om kontoen hennes. En enhet
              som ALLEREDE er registrert vises likevel, og kan fjernes: det er nettopp det
              den som sitter med en ubrukelig metode trenger. */}
          {enhet || enhetTilbys ? (
            <Metoderad
              type="enhet"
              lagtTil={enhet?.lagtTil ?? null}
              grunn={enhet && antall === 1 ? siste : !enhet && !enhetMulig ? UTEN_STOETTE : null}
              handling={
                enhet ? (
                  antall > 1 ? (
                    radmeny(enhet)
                  ) : (
                    <KanIkkeFjernes />
                  )
                ) : enhetMulig ? (
                  // Aldri en død knapp: uten støtte i nettleseren finnes den ikke i det hele
                  // tatt, og grunnen står i raden i stedet.
                  <button type="button" className="panel-knapp-lenke" aria-label="Legg til denne enheten" disabled={opptatt} onClick={leggTilEnhet} data-testid="legg-til-enhet">
                    <Ikon navn="add-outline" />
                    Legg til
                  </button>
                ) : null
              }
            />
          ) : null}

          <Metoderad
            type="app"
            lagtTil={app?.lagtTil ?? null}
            grunn={app && antall === 1 ? siste : null}
            handling={
              app ? (
                antall > 1 ? (
                  radmeny(app)
                ) : (
                  <KanIkkeFjernes />
                )
              ) : (
                <button
                  type="button"
                  className="panel-knapp-lenke"
                  aria-expanded={aapen}
                  aria-controls={aapen ? 'oppsett-app' : undefined}
                  aria-label={aapen ? 'Lukk koden fra appen' : 'Legg til koden fra appen'}
                  onClick={() => setAapen((a) => !a)}
                  data-testid="legg-til-app"
                >
                  <Ikon navn={aapen ? 'close-outline' : 'add-outline'} />
                  {aapen ? 'Lukk' : 'Legg til'}
                </button>
              )
            }
          >
            {aapen && !app ? (
              <div className="panel-rad-innst-panel" id="oppsett-app" data-testid="oppsett-app">
                {startfeil ? (
                  <>
                    <p role="alert" className="panel-feltfeil">
                      <Ikon navn="alert-circle-outline" />
                      {startfeil}
                    </p>
                    <div>
                      <button type="button" className="panel-knapp-lenke" onClick={paaNytt}>
                        Prøv igjen
                      </button>
                    </div>
                  </>
                ) : (
                  <form onSubmit={fullfoerApp} className="panel-skjema panel-min-oppsett">
                    {/* Helten først, som foran låsen: QR-en, eller nøkkelen for den som ikke
                        kan skanne. Ingen forklaring av hvorfor panelet krever to steg — den
                        som er her er innlogget og har lest den. */}
                    <Oppsettshelt oppsett={oppsett} visning={visning} />
                    <Kodefelt id="min-innlogging-kode" kode={kode} onKode={setKode} feil={kodefeil} av={opptatt || !oppsett} synligEtikett />
                    <button type="submit" className="panel-knapp" disabled={opptatt || !oppsett}>
                      Fullfør oppsettet
                    </button>
                    <div className="panel-lenker">
                      <Visningsknapp oppsett={oppsett} visning={visning} onVisning={setVisning} />
                    </div>
                  </form>
                )}
              </div>
            ) : null}
          </Metoderad>
        </Seksjon>
      </div>

      {/* Bekreftelsen, aldri en angre-toast: avregistreringen skjer hos leverandøren med én
          gang og kan ikke tas tilbake. Den sier hva som gjelder ETTERPÅ — det er
          opplysningen brukeren mangler, ikke at hun trykket. */}
      {fjerner ? (
        <Dialog tittelId="fjern-h" onLukk={() => setFjerner(null)} testid="fjern-dialog">
          <h2 id="fjern-h" className="panel-h2">
            <Ikon navn="trash-outline" /> Fjern {NAVN[fjerner.type]}?
          </h2>
          <p className="panel-tekst">Etterpå bekrefter du alltid med {NAVN[fjerner.type === 'enhet' ? 'app' : 'enhet']}.</p>
          <p className="panel-tekst">Vil du bruke den igjen, må du legge den til på nytt.</p>
          <div className="panel-handlinger">
            <Knapp label="Fjern" fare grunn={opptatt ? 'Fjerner …' : null} onClick={() => fjern(fjerner)} testid="fjern-bekreft" />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setFjerner(null)} testid="fjern-avbryt" />
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
